import { useState } from 'react';
import { OUTSIDER_NAME_MAX, normalizeOutsiderName } from '@topspin/shared';
import type { Session } from '@topspin/shared';
import { ApiError } from '../api.js';
import { useCreateFill, type GroupView } from '../data.js';
import { useOnline } from '../hooks.js';

/**
 * "Add sub" (§8a) — the name of somebody from outside the group, for one of a short-handed
 * night's empty court slots (§9.1).
 *
 * A name and nothing else, which is what makes it a different sheet from the out flow (§8)
 * rather than a variant of it: there is nobody going out, so there is no slot to point at and
 * no reserve to offer first. Whoever went and found a fourth is simply writing down who it is,
 * and which of the empty slots it lands in is the server's business.
 */
export default function AddSubFlow({
  group,
  seasonId,
  session,
  onClose,
}: {
  group: GroupView;
  seasonId: string;
  session: Session;
  onClose: () => void;
}) {
  const [name, setName] = useState('');
  const online = useOnline();
  const create = useCreateFill();

  const trimmed = normalizeOutsiderName(name);
  const canSave = online && !create.isPending && trimmed.length > 0;
  const error = create.error as Error | undefined;

  async function save() {
    try {
      await create.mutateAsync({
        key: group.key,
        input: {
          seasonId,
          groupId: group.config.groupId,
          date: session.date,
          name: trimmed,
        },
      });
      onClose();
    } catch {
      // Rendered below from the mutation's error; the sheet stays open so the typing isn't lost.
    }
  }

  return (
    <div className="sheet-backdrop" role="dialog" aria-modal="true">
      <div className="sheet">
        <h2>Add sub</h2>
        <p className="muted">Record the name of the sub filling in this week.</p>
        <input
          className="text-input"
          type="text"
          autoFocus
          placeholder="Their name"
          maxLength={OUTSIDER_NAME_MAX}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />

        {!online && <p className="error">You're offline — this can't be saved right now.</p>}
        {error && <p className="error">{messageFor(error)}</p>}

        <div className="actions">
          <button type="button" className="primary" disabled={!canSave} onClick={save}>
            {create.isPending ? 'Saving…' : 'Save'}
          </button>
          <button type="button" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function messageFor(error: Error): string {
  if (error instanceof ApiError && error.code === 'season-rolled-over') {
    return 'The schedule has been updated. Reload the app and try again.';
  }
  // Two people both went and found a fourth. The name that landed first is on the card once
  // the refetch lands, so this says the night is full rather than offering a retry.
  if (error instanceof ApiError && error.code === 'slot-filled') {
    return 'Somebody else just filled that slot. Reload to see who.';
  }
  if (error instanceof ApiError) return error.message;
  return "Couldn't save that — check your connection and try again.";
}
